import React, { useState, useRef, useEffect } from 'react';
import { Navbar, Container, Nav, Dropdown } from "react-bootstrap";
import { FaStickyNote } from 'react-icons/fa';
import stickyNotesIcon from "assets/images/ico/stickyNotes.png.jpg";
import StickyNotes from "components/controls/stickyNotes/StickyNotes";
import NotificationActionApproveReject from "components/NotificationActionApproveReject";
import { NotificationsGet, NotificationsShown, notificationsRead } from "apiServices/reducer/apiService";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStickyNotes, saveStickyNotes, deleteStickyNotes, setSelectedFamilyMember } from "apiServices/reducer/apiService";

import './CTNavbar.css';

const RISK_CALCULATOR_URL = 'https://tools.acc.org/ascvd-risk-estimator-plus/#!/calculate/estimate/';

const menuLinkHref = (item) => {
    if (!item) return '/landing-page';
    const url = String(item.url || '').trim();
    if (/^https?:\/\//i.test(url)) return url;
    if (item.menu_id === 24 || item.menu_id === 1019) return RISK_CALCULATOR_URL;
    const slug = String(item.id || '').trim();
    if (slug) return `/landing-page?id=${encodeURIComponent(slug)}`;
    return `/landing-page?menuId=${encodeURIComponent(item.menu_id)}`;
};

const isExternalHref = (href) => /^https?:\/\//i.test(href);

const CTNavbar = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [visibleSubmenu, setVisibleSubmenu] = useState(null);
    const [activeSubmenuItem, setActiveSubmenuItem] = useState(null);
    const [activeParentItem, setActiveParentItem] = useState(null);
    const [showStickyNotes, setShowStickyNotes] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [rowData, setRowData] = useState({});
    const [prevId, setPrevId] = useState(null);
    const intervalRef = useRef(null);
    const [shownNotificationIds, setShownNotificationIds] = useState(new Set());
    const [readNotificationIds, setReadNotificationIds] = useState(new Set());
    const [isStickyNoteVisible, setIsStickyNoteVisible] = useState(false);
    const [stickyNotes, setStickyNotes] = useState([]);

    // Add timeout refs for better hover control
    const hoverTimeoutRef = useRef(null);

    // Add ref for navbar elements
    const navbarRef = useRef(null);

    const access = useSelector((state) => state.apiService.sharedReportsValidateCode);
    const externalUser = useSelector((state) => (state.apiService.login != undefined) ? state.apiService.login.is_external_user : false);
    const isAccess = props.isAccess;

    // Get selected family member from Redux store
    const selectedFamilyMemberFromStore = useSelector((state) => state.apiService.selectedFamilyMember);
    const [selectedFamilyMember, setSelectedFamilyMemberLocal] = useState(selectedFamilyMemberFromStore);
    const dispatch = useDispatch();

    // Sync local state with Redux store
    useEffect(() => {
        if (selectedFamilyMemberFromStore && selectedFamilyMemberFromStore !== selectedFamilyMember) {
            setSelectedFamilyMemberLocal(selectedFamilyMemberFromStore);
        }
    }, [selectedFamilyMemberFromStore, selectedFamilyMember]);
    const [selectedMember, setSelectedMember] = useState(null);
    const menuItems = React.useMemo(() => {
        let items = [];
        if (props.navItems && props.navItems.length > 0) {
            items = props.navItems;
        } else if (access && access.menuItems && Array.isArray(access.menuItems)) {
            items = access.menuItems.map(item => ({
                menu_id: item.menu_id,
                id: item.id || '',
                title: item.title || "Untitled",
                url: item.url || "#",
                parent_id: item.parent_id || null,
                sort_order: item.sort_order || 0,
                type: item.type || "item"
            }));
        }

        return items;
    }, [props.navItems, access, isAccess]);

    useEffect(() => {
        if (!activeParentItem && !activeSubmenuItem && menuItems.length > 0) {
            const defaultItem = menuItems[0];

            if (defaultItem) {
                props.setDashboard(defaultItem);
                setActiveParentItem(defaultItem.menu_id);
            }
        }
    }, [menuItems, activeParentItem, activeSubmenuItem, props]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const slug = params.get('id');
        const menuId = Number(params.get('menuId'));
        const item = slug
            ? menuItems.find((entry) => String(entry.id) === slug)
            : (menuId ? menuItems.find((entry) => Number(entry.menu_id) === menuId) : null);
        if (item && menuItems.length) {
            if (item.parent_id) {
                setActiveParentItem(Number(item.parent_id));
                setActiveSubmenuItem(item.id || item.menu_id);
            } else {
                setActiveParentItem(Number(item.menu_id));
                setActiveSubmenuItem(null);
            }
            return;
        }
        if (!slug && !params.get('menuId') && !params.get('menu')) {
            if (props.activeMenuItem?.showReports || Number(props.activeTab?.parent_id) === 2) {
                const categoryTitle = props.activeTab?.title || props.activeTab?.name || '';
                const child = menuItems.find((entry) =>
                    Number(entry.parent_id) === 2 &&
                    categoryTitle &&
                    String(entry.title).toLowerCase() === String(categoryTitle).toLowerCase()
                );
                setActiveParentItem(2);
                setActiveSubmenuItem(child ? (child.id || child.menu_id) : null);
                return;
            }
            setActiveParentItem(1);
            setActiveSubmenuItem(null);
            return;
        }

        if (props.activeMenuItem?.showDashBoard) {
            setActiveParentItem(1);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showReports) {
            setActiveParentItem(2);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showSharedReports) {
            setActiveParentItem(4);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showNotes) {
            setActiveParentItem(21);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showAskAcme) {
            setActiveParentItem(28);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showBlog) {
            setActiveParentItem(5);
            setActiveSubmenuItem(null);
            return;
        }
        if (props.activeMenuItem?.showSecondOpinion) {
            setActiveParentItem(8);
            setActiveSubmenuItem(null);
            return;
        }

        if (props.activeParentId === 2) {
            setActiveParentItem(props.activeParentId);
        } else if (props.activeTab && props.activeTab.parent_id === 9) {
            setActiveParentItem(props.activeParentId);
        } else if (props.activeTab && props.activeTab.parent_id > 0) {
            setActiveParentItem(props.activeTab.parent_id);
        } else if (props.activeTab && props.activeTab.menu_id) {
            setActiveParentItem(props.activeTab.menu_id);
        }
    }, [location.search, menuItems, props.activeParentId, props.activeTab, props.activeMenuItem]);

    const handleMouseEnter = (menuId) => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setVisibleSubmenu(menuId);
    };

    const handleMouseLeave = () => {
        // Add a small delay before hiding to allow moving to dropdown menu
        hoverTimeoutRef.current = setTimeout(() => {
            setVisibleSubmenu(null);
        }, 150);
    };

    const handleDropdownEnter = () => {
        // Clear timeout when entering dropdown menu
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    };

    const handleDropdownLeave = () => {
        // Hide dropdown when leaving the menu
        setVisibleSubmenu(null);
    };

    const rememberMenuInHistory = (href) => {
        if (!href || isExternalHref(href)) return;
        if (location.pathname !== '/landing-page' && location.pathname !== '/admin-landing-page') return;
        const current = `${location.pathname}${location.search}`;
        if (current === href) return;
        navigate(href);
    };

    const handleSubmenuItemClick = (subItem) => {
        if (subItem.menu_id === 24||subItem.menu_id === 1019) {
            window.open('https://tools.acc.org/ascvd-risk-estimator-plus/#!/calculate/estimate/', '_blank');
            return;
        }

        // Reset archive state by passing it through setDashboard
        const subItemWithReset = {
            ...subItem,
            resetArchive: true  // Add a flag to indicate archive should be reset
        };

        props.setDashboard(subItemWithReset);
        rememberMenuInHistory(menuLinkHref(subItem));
        setActiveSubmenuItem(subItem.id || subItem.menu_id);
        setActiveParentItem(subItem.parent_id);
        setVisibleSubmenu(null);

        // Close mobile navbar when submenu item is clicked
        if (navbarRef.current) {
            const navbarToggler = navbarRef.current.querySelector('.navbar-toggler');
            const navbarCollapse = navbarRef.current.querySelector('.navbar-collapse');

            if (navbarToggler && navbarCollapse) {
                // Check if navbar is expanded (mobile menu is open)
                if (!navbarToggler.classList.contains('collapsed') && navbarCollapse.classList.contains('show')) {
                    navbarToggler.click(); // This will close the mobile menu
                }
            }
        }
    };

    const renderNavLink = (item) => {
        if (!item) return null;

        const isActive = Number(activeParentItem) === Number(item.menu_id);
        const href = menuLinkHref(item);
        const external = isExternalHref(href);

        return (
            <Nav.Link
                key={`parent-${item.menu_id}-${item.title}`}
                href={href}
                active={false}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                    e.preventDefault();
                    if (external) {
                        window.open(href, '_blank', 'noopener,noreferrer');
                        return;
                    }

                    props.setDashboard(item);
                    rememberMenuInHistory(href);
                    // Always highlight the clicked item (do not keep Your Reports stuck)
                    setActiveParentItem(item.menu_id);
                    setActiveSubmenuItem(null);

                    // Close mobile navbar when nav link is clicked
                    if (navbarRef.current) {
                        const navbarToggler = navbarRef.current.querySelector('.navbar-toggler');
                        const navbarCollapse = navbarRef.current.querySelector('.navbar-collapse');

                        if (navbarToggler && navbarCollapse) {
                            // Check if navbar is expanded (mobile menu is open)
                            if (!navbarToggler.classList.contains('collapsed') && navbarCollapse.classList.contains('show')) {
                                navbarToggler.click(); // This will close the mobile menu
                            }
                        }
                    }
                }}
                className={`fw-bold font-inter nav-link-custom ${isActive ? "menu-selected" : ""}`}
                style={{
                    cursor: "pointer",
                    background: isActive ? "var(--pmr-btn-primary)" : "none",
                    color: isActive ? "var(--pmr-white)" : "var(--pmr-text-dark)",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    letterSpacing: "0.025em",
                    textTransform: "none",
                    textDecoration: "none",
                    position: "relative",
                    overflow: "hidden",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    margin: "2px",
                    whiteSpace: "nowrap"
                }}
            >
                {item.title}
            </Nav.Link>
        );
    };

    const renderDropdown = (item) => {
        const subItems = menuItems.filter((subItem) => subItem.parent_id === item.menu_id);
        const isActive = Number(activeParentItem) === Number(item.menu_id);
        const isOpen = visibleSubmenu === item.menu_id;

        return (
            <div
                key={item.menu_id}
                className="dropdown-container"
                onMouseEnter={() => handleMouseEnter(item.menu_id)}
                onMouseLeave={handleMouseLeave}
            >
                <Dropdown show={isOpen}>
                    <Dropdown.Toggle
                        variant="link"
                        className={`fw-bold font-inter dropdown-toggle-custom d-flex align-items-center justify-content-between ${isActive ? "menu-selected" : ""}`}
                        style={{
                            background: isActive ? "var(--pmr-btn-primary)" : "none",
                            color: isActive ? "var(--pmr-white)" : "var(--pmr-text-dark)",
                            fontWeight: "600",
                            fontSize: "14px",
                            transition: "background-color 0.15s ease, color 0.15s ease",
                            letterSpacing: "0.025em",
                            textDecoration: "none",
                            border: "none",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            transition: "all 0.3s ease",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            gap: "8px"
                        }}
                    >
                        <span style={{ flex: 1, textAlign: "left" }}>{item.title}</span>
                        <i className="fas fa-chevron-down" style={{
                            fontSize: "12px",
                            flexShrink: 0,
                            transition: "transform 0.3s ease",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
                        }}></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                        className="p-2 customsubmenu shadow-lg"
                        onMouseEnter={handleDropdownEnter}
                        onMouseLeave={handleDropdownLeave}
                        style={{
                            maxHeight: "none",
                            opacity: isOpen ? 1 : 0,
                            transform: "none",
                            pointerEvents: isOpen ? "auto" : "none",
                            visibility: isOpen ? "visible" : "hidden",
                            transition: "opacity 0.15s ease",
                            border: "1px solid #e9ecef",
                            borderRadius: "12px",
                        }}
                    >
                        {subItems.length > 0 ? (
                            subItems.map((subItem) => {
                                const href = menuLinkHref(subItem);
                                const external = isExternalHref(href);
                                return (
                                <Dropdown.Item
                                    key={subItem.menu_id}
                                    href={href}
                                    target={external ? '_blank' : undefined}
                                    rel={external ? 'noopener noreferrer' : undefined}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSubmenuItemClick(subItem);
                                    }}
                                    className={`mb-1 text-dark dropdownitem ${Number(activeSubmenuItem) === Number(subItem.menu_id) || String(activeSubmenuItem) === String(subItem.id) ? 'active' : ''}`}
                                    style={{
                                        backgroundColor: Number(activeSubmenuItem) === Number(subItem.menu_id) || String(activeSubmenuItem) === String(subItem.id) ? 'var(--pmr-bg-subtle)' : 'transparent',
                                        color: Number(activeSubmenuItem) === Number(subItem.menu_id) || String(activeSubmenuItem) === String(subItem.id) ? 'var(--pmr-primary)' : 'var(--pmr-text)',
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        margin: "2px 0",
                                        whiteSpace: "normal",
                                        overflow: "visible",
                                        transition: "background-color 0.15s ease, color 0.15s ease"
                                    }}
                                >
                                    {subItem.title}
                                </Dropdown.Item>
                                );
                            })
                        ) : (
                            <Dropdown.Item href="/landing-page" onClick={(e) => e.preventDefault()} disabled className="text-black" style={{
                                fontSize: "13px",
                                fontWeight: "600"
                            }}>
                                <i className="fas fa-info-circle me-2"></i>
                                No SubItems Available
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    useEffect(() => {
        if (props.familyItems && props.familyItems.length > 0 && !selectedFamilyMember) {
            // Try to get stored family member from Redux first
            const storedMember = selectedFamilyMemberFromStore;
            
            // Check if stored member exists in current family items
            const validStoredMember = storedMember && 
                props.familyItems.some(member => member.user_id === storedMember.user_id);
            
            if (validStoredMember) {
                // Use stored member if it's still valid
                setSelectedFamilyMemberLocal(storedMember);
                if (props.onFamilyMemberClick) {
                    props.onFamilyMemberClick(storedMember.user_id);
                }
            } else {
                // Fall back to first member if stored member is invalid or doesn't exist
                const defaultMember = props.familyItems[0];
                setSelectedFamilyMemberLocal(defaultMember);
                dispatch(setSelectedFamilyMember(defaultMember)); // Store in Redux
                if (props.onFamilyMemberClick) {
                    props.onFamilyMemberClick(defaultMember.user_id);
                }
            }
        }
    }, [props.familyItems, selectedFamilyMemberFromStore, dispatch]);

    const handleFamilyMemberChange = async (member) => {
        if (!member || !member.user_id) {
            console.error('Invalid family member selected');
            return;
        }

        try {
            // Update both local state and Redux store
            setSelectedFamilyMemberLocal(member);
            setSelectedMember(member);
            dispatch(setSelectedFamilyMember(member)); // Store in Redux

            // Check if there's a currently active menu item that should be preserved
            const hasActiveMenu = props.activeMenuItem && Object.keys(props.activeMenuItem).some(key => 
                key !== 'showDashBoard' && props.activeMenuItem[key] === true
            );

            if (!hasActiveMenu) {
                // Only reset active menu states if no specific menu item is currently active
                setActiveParentItem(null);
                setActiveSubmenuItem(null);
                
                // Only navigate to dashboard if no specific menu item is currently active
                const dashboardItem = menuItems.find(item => item.menu_id === 1); // 1 is the dashboard menu_id
                if (dashboardItem) {
                    props.setDashboard(dashboardItem);
                }
            }
            // If there's an active menu item, don't change the navigation - preserve the current state

            // Log the selected member for debugging
            console.log('Selected Family Member:', {
                user_id: member.user_id,
                name: member.dropdown_display_name,
                timestamp: new Date().toISOString()
            });

            // Reset sticky notes visibility and data
            setIsStickyNoteVisible(false);
            setStickyNotes([]);

            // Reset notifications
            setShowModal(false);
            setRowData({});
            setShownNotificationIds(new Set());
            setReadNotificationIds(new Set());

            // Call the parent component's callback with the selected user ID
            if (props.onFamilyMemberClick) {
                await props.onFamilyMemberClick(member.user_id);
            }

            // Hide dropdown after selection
            setVisibleSubmenu(null);

            // Fetch fresh data for the selected family member
            if (member.user_id && token) {
                // Fetch notifications for the selected family member
                dispatch(NotificationsGet({
                    user_id: member.user_id,
                    token: token
                }));

                // Sticky notes are intentionally NOT auto-fetched/shown on family member change.
                // They will be fetched and displayed only when the user clicks the sticky notes icon.

                // Reset and restart notification polling for the new user
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
                fetchNotifications();
                intervalRef.current = setInterval(() => {
                    fetchNotifications();
                }, 1000 * 60 * timeInMinutes);
            }

        } catch (error) {
            console.error('Error updating family member data:', error);
        }
    };

    const userData = useSelector((state) => state.apiService.login);
    const [showBlinkingDot, setShowBlinkingDot] = useState(false);

    // Add effect to check if selected member is different from current user
    useEffect(() => {
        if (selectedFamilyMember && userData?.user_id) {
            setShowBlinkingDot(selectedFamilyMember.user_id !== userData.user_id);
        } else {
            setShowBlinkingDot(false);
        }
    }, [selectedFamilyMember, userData]);

    const renderFamilyDropdown = () => {
        const isOpen = visibleSubmenu === 'family';

        return (
            <div
                className="family-dropdown-container"
                onMouseEnter={() => handleMouseEnter('family')}
                onMouseLeave={handleMouseLeave}
            >
                {showBlinkingDot && <div className="blinking-dot" />}
                <Dropdown show={isOpen} align="end">
                    <Dropdown.Toggle
                        variant="link"
                        className={`fw-bold dropdowntoggle text-dark custom-family-toggle d-flex align-items-center justify-content-between ${showBlinkingDot ? 'has-different-member' : ''}`}
                        style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#495057",
                            backgroundColor: "white",
                            transition: "all 0.3s ease",
                            whiteSpace: "nowrap",
                            minWidth: "180px",
                            gap: "8px"
                        }}
                    >
                        <span className="text-truncate" style={{ maxWidth: "140px", flex: 1, textAlign: "left" }}>
                            {selectedFamilyMember
                                ? selectedFamilyMember.dropdown_display_name
                                : "Select Family Member"}
                        </span>
                        <i className="fas fa-chevron-down" style={{
                            fontSize: "12px",
                            flexShrink: 0,
                            transition: "transform 0.3s ease",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
                        }}></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                        className="p-2 customsubmenu family-dropdown-menu shadow-lg"
                        onMouseEnter={handleDropdownEnter}
                        onMouseLeave={handleDropdownLeave}
                        style={{
                            width: "250px",
                            opacity: isOpen ? 1 : 0,
                            transform: isOpen ? "translateY(0)" : "translateY(-10px)",
                            pointerEvents: isOpen ? "auto" : "none",
                            visibility: isOpen ? "visible" : "hidden",
                            transition: "all 0.3s ease",
                            border: "1px solid #e9ecef",
                            borderRadius: "12px"
                        }}
                    >
                        {props.familyItems && props.familyItems.length > 0 ? (
                            props.familyItems.map((member) => (
                                <Dropdown.Item
                                    key={member.user_id}
                                    href="/landing-page"
                                    className={`family-dropdown-item d-flex align-items-center justify-content-between ${selectedFamilyMember?.user_id === member.user_id ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFamilyMemberChange(member);
                                    }}
                                    style={{
                                        backgroundColor: selectedFamilyMember?.user_id === member.user_id ? 'var(--pmr-bg-subtle)' : 'transparent',
                                        color: selectedFamilyMember?.user_id === member.user_id ? 'var(--pmr-primary)' : 'var(--pmr-text)',
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        margin: "2px 0",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    <span className="text-truncate">{member.dropdown_display_name}</span>
                                    {selectedFamilyMember?.user_id === member.user_id && (
                                        <i className="fas fa-check ms-2"></i>
                                    )}
                                </Dropdown.Item>
                            ))
                        ) : (
                            <Dropdown.Item href="/landing-page" onClick={(e) => e.preventDefault()} disabled className="family-dropdown-item text-muted">
                                <i className="fas fa-info-circle me-2"></i>
                                No Family Members Available
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const token = useSelector((state) => state.apiService.token);
    const timeInMinutes = 0.6;

    useEffect(() => {
        // console.log('Current user data:', userData);
        // console.log('Current token:', token);
    }, [userData, token]);

    useEffect(() => {
        const isLoggedIn = userData && userData.user_id && userData.Token && token && token.length > 0;
        if (isLoggedIn) {
            fetchNotifications();
            intervalRef.current = setInterval(() => {
                fetchNotifications();
            }, 1000 * 60 * timeInMinutes);
        } else {
            setShowModal(false);
            setRowData({});
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [token, userData, dispatch]);

    // Sticky notes are no longer fetched/shown on initial login.
    // They will be fetched and displayed only when the user clicks the sticky notes icon.

    const markNotificationAsShown = async (notificationId) => {
        if (!userData?.user_id || !userData?.Token || shownNotificationIds.has(notificationId)) {
            return;
        }

        try {
            // Call NotificationsShown API
            await dispatch(NotificationsShown({
                user_id: userData.user_id,
                notification_id: notificationId,
                token: userData.Token
            })).unwrap();

            // Update local state
            setShownNotificationIds(prev => new Set([...prev, notificationId]));
        } catch (error) {
            console.error("Error marking notification as shown:", error);
        }
    };

    const markNotificationAsRead = async (notificationId) => {
        if (!userData?.user_id || !userData?.Token || readNotificationIds.has(notificationId)) {
            return;
        }

        try {
            // Call notificationsRead API
            await dispatch(notificationsRead({
                user_id: userData.user_id,
                notification_id: notificationId,
                token: userData.Token
            })).unwrap();

            // Update local state
            setReadNotificationIds(prev => new Set([...prev, notificationId]));
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const fetchNotifications = () => {
        const userId = selectedFamilyMember?.user_id || userData?.user_id;
        if (userId && userData?.Token) {
            dispatch(NotificationsGet({ user_id: userId, token: userData.Token }))
                .then((resp) => {
                    const notifArr = Array.isArray(resp.payload) ? resp.payload : resp.payload?.Table;
                    if (notifArr && notifArr.length > 0) {
                        const important = notifArr.filter(m =>
                            (
                                ((m.share_id && m.share_id !== '') && (!m.report_id || m.report_id === '')) ||
                                (m.family_account_id && m.family_account_id !== '')
                            ) &&
                            !m.is_displayed &&
                            !shownNotificationIds.has(m.notification_id) &&
                            !readNotificationIds.has(m.notification_id)
                        );

                        if (important.length > 0) {
                            const topMessage = important[0];
                            setRowData(topMessage);
                            setShowModal(true);
                            markNotificationAsShown(topMessage.notification_id);
                        }
                    }
                });
        }
    };

    const handleImportantNotificationClick = async (message) => {
        if (!message?.notification_id) return;

        setRowData(message);
        setShowModal(true);

        // Mark as read when user clicks the notification
        await markNotificationAsRead(message.notification_id);
    };

    const fetchStickyNotes = (onIconClick = false) => {
        const userId = selectedFamilyMember?.user_id || userData?.user_id;

        if (!userId) {
            console.error('User not authenticated - user_id missing');
            return;
        }

        const params = {
            LoginUserID: +userId.toString(),
            Notes: '',
            stickyID: null,
            positionX: "",
            positionY: "",
            token: token
        };

        dispatch(getStickyNotes(params))
            .then((result) => {
                if (result.payload) {
                    const notesData = Array.isArray(result.payload) ? result.payload : result.payload.Table;
                    if (notesData && notesData.length > 0) {
                        setStickyNotes(notesData);
                        // Check if any note has content to determine initial visibility
                        const hasContent = notesData.some(note => note.notes && note.notes.trim() !== '');
                        if (onIconClick) {
                            // If user clicked the icon, always show
                            setIsStickyNoteVisible(true);
                        } else {
                            // On initial load, only show if there's content and not already visible
                            if (!isStickyNoteVisible) {
                                setIsStickyNoteVisible(hasContent);
                            }
                        }
                    } else {
                        if (onIconClick) {
                            // If user clicked the icon and there are no notes, create a new one immediately
                            addNewStickyNote();
                        } else {
                            setStickyNotes([]);
                            setIsStickyNoteVisible(false);
                        }
                    }
                } else {
                    // Handle case when result.payload is null/undefined
                    if (onIconClick) {
                        addNewStickyNote();
                    } else {
                        setStickyNotes([]);
                        setIsStickyNoteVisible(false);
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching sticky notes:', error);
                if (onIconClick) {
                    // If there's an error but user clicked the icon, try to create a new note
                    addNewStickyNote();
                } else {
                    setStickyNotes([]);
                    setIsStickyNoteVisible(false);
                }
            });
    };

    const addNewStickyNote = () => {
        const userId = selectedFamilyMember?.user_id || userData?.user_id;

        if (!userId) {
            console.error('User not authenticated - user_id missing');
            return;
        }

        const params = {
            UserID: userId.toString(),
            Notes: '',
            stickyID: null,
            position_x: window.innerWidth - 320,
            position_y: 70,
            token: token
        };

        dispatch(saveStickyNotes(params))
            .then((result) => {
                console.log('New sticky note created:', result);
                // Ensure visibility is set to true when creating a new note
                setIsStickyNoteVisible(true);

                // Add the new note to the state immediately
                if (result.payload && result.payload.Table && result.payload.Table.length > 0) {
                    const newNote = result.payload.Table[0];
                    setStickyNotes(prevNotes => [...prevNotes, newNote]);
                } else {
                    // If no note in response, fetch to get the latest
                    fetchStickyNotes();
                }
            })
            .catch(error => {
                console.error('Error adding sticky note:', error);
                // Even if there's an error, ensure visibility is maintained if user clicked the icon
                setIsStickyNoteVisible(true);
            });
    };

    const handleStickyNotesClick = () => {
        // Toggle visibility
        if (!isStickyNoteVisible) {
            // If closed, open and fetch notes
            setIsStickyNoteVisible(true);
            fetchStickyNotes(true); // Pass true to indicate icon click
        } else {
            // If open, close
            setIsStickyNoteVisible(false);
        }
    };

    const handleStickyNotePositionChange = (position) => {
        if (stickyNotes.length > 0) {
            const updatedNotes = stickyNotes.map(note => {
                if (note.sticky_id === position.stickyID) {
                    return { ...note, position: position };
                }
                return note;
            });
            setStickyNotes(updatedNotes);
        }
    };

    const handleCloseStickyNote = (id) => {
        // Filter out the closed note from the state
        setStickyNotes(prevNotes => {
            const updatedNotes = prevNotes.filter(note => note.sticky_id !== id);

            // If no notes remain, hide sticky notes
            if (updatedNotes.length === 0) {
                setIsStickyNoteVisible(false);
            }

            return updatedNotes;
        });
    };

    const handleDeleteNote = async (noteId, index) => {
        try {
            const userId = selectedFamilyMember?.user_id || userData?.user_id;
            const params = {
                token: token,
                user_id: userId,
                note_id: noteId
            };
            const result = await dispatch(deleteStickyNotes(params));
            if (result.payload) {
                // Update sticky notes state by filtering out the deleted note
                setStickyNotes(prev => {
                    const updatedNotes = prev.filter(note => note.sticky_id !== noteId);

                    // If this was the last note, close sticky notes
                    if (updatedNotes.length === 0) {
                        setIsStickyNoteVisible(false);
                    }

                    return updatedNotes;
                });
            }
        } catch (error) {
            console.error("Error deleting sticky note:", error);
        }
    };

    const handleActionCompleted = () => {
        fetchNotifications();
        const event = new Event('notificationsUpdated');
        document.dispatchEvent(event);
        console.log('handleActionCompleted', event);
    }

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Navbar ref={navbarRef} expand="lg" className="custom-navbar rounded shadow-sm py-2" >
            <Container fluid>
                <Navbar.Toggle aria-controls="navbarColor01" />
                <Navbar.Collapse id="navbarColor01" className="justify-content-between">
                    <Nav className="me-auto align-items-start flex-wrap">
                        {menuItems.length > 0 ? (
                            menuItems.map((item) => {
                                // if (item.menu_id === 1 || item.menu_id === 9 || item.menu_id === 10) {
                                //     return renderNavLink(item);
                                // }

                                return item.type === "item" && item.parent_id === null
                                    ? renderNavLink(item)
                                    : item.type === "collapsible"
                                        ? renderDropdown(item)
                                        : null;
                            })
                        ) : (
                            <div className="text-muted">No menu items available</div>
                        )}
                    </Nav>

                    <div className="navbar-right-controls d-flex align-items-center">
                        {props.familyItems !== undefined && props.familyItems.length > 0 && (
                            renderFamilyDropdown()
                        )}

                        {!isAccess && externalUser !== true && (
                            <div className="me-2 position-relative">
                                <img
                                    src={stickyNotesIcon}
                                    alt="Sticky Notes"
                                    style={{
                                        cursor: "pointer",
                                        width: "24px",
                                        height: "24px",
                                        marginLeft: "8px"
                                    }}
                                    title="Toggle Sticky Notes"
                                    onClick={handleStickyNotesClick}
                                />
                                {(isStickyNoteVisible && stickyNotes.length > 0) && (
                                    [...stickyNotes]
                                        .sort((a, b) => a.sticky_id - b.sticky_id)
                                        .map((sticky, idx) => (
                                            <StickyNotes
                                                key={sticky.sticky_id || idx}
                                                index={idx}
                                                sticky={sticky}
                                                onClose={handleCloseStickyNote}
                                                onAdd={addNewStickyNote}
                                                onDelete={handleDeleteNote}
                                                onUpdateData={handleStickyNotePositionChange}
                                                userId={selectedFamilyMember?.user_id || userData?.user_id}
                                            />
                                        ))
                                )}
                            </div>
                        )}
                    </div>
                </Navbar.Collapse>
            </Container>

            <NotificationActionApproveReject
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
                rowData={rowData}
                onActionComplete={handleActionCompleted}
            />
        </Navbar>
    );
};

export default CTNavbar;